import {
	type BreakEventRequest,
	previewBreakCalendarEvent,
	upsertCalendarEvent,
} from "@/shared/repositories/CalendarEventRepository";
import type { PlanChangeRef } from "@/shared/repositories/PlanChangeRepository";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";

type Props = {
	request: BreakEventRequest;
	onSaved: (refs: PlanChangeRef[]) => void;
};

export const useBreakPlanConfirmation = ({ request, onSaved }: Props) => {
	const [error, setError] = useState("");
	const [planChanged, setPlanChanged] = useState(false);
	const preview = useQuery({
		queryKey: ["breakPlanPreview", request],
		queryFn: ({ signal }) => previewBreakCalendarEvent(request, signal),
		cacheTime: 0,
		staleTime: 0,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		retry: false,
		onError: (e: Error) => setError(e.message),
	});
	const save = useMutation({
		mutationFn: (planToken: string) =>
			upsertCalendarEvent({
				...request,
				eventTypeCode: "B",
				expectedBreakPlanToken: planToken,
			}),
		onSuccess: (result) => onSaved(result.planChanges),
		onError: async (e: Error) => {
			if (e.message.includes("The break plan changed")) {
				setPlanChanged(true);
				await preview.refetch();
			} else {
				setError(e.message);
			}
		},
	});
	const rows = preview.data ?? [];
	const confirm = () => {
		if (
			save.isLoading ||
			preview.isFetching ||
			preview.isError ||
			!rows[0]?.PlanToken
		)
			return;
		setError("");
		save.mutate(rows[0].PlanToken);
	};

	return {
		rows,
		saving: save.isLoading,
		previewLoading: preview.isFetching,
		previewFailed: preview.isError,
		planChanged,
		error,
		clearError: () => setError(""),
		reload: () => preview.refetch(),
		confirm,
	};
};
